import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { DocumentDto, DocumentDtoDocuTypeEnum } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import notificationStore from "../../stores/Notification/notificationStore";

interface ExistingDocumentPickerProps {
  patientId: number;
  onSelect: (documentId: string, documentName: string) => void;
  excludeDocumentIds?: string[];
  disabled?: boolean;
}

export interface ExistingDocumentPickerRef {
  open: () => void;
}

const ExistingDocumentPicker = forwardRef<
  ExistingDocumentPickerRef,
  ExistingDocumentPickerProps
>(({ patientId, onSelect, excludeDocumentIds = [], disabled = false }, ref) => {
  const { documents: documentsApi } = useApi();
  const [documentList, setDocumentList] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => {
      if (!loading && availableDocuments.length > 0) {
        setSelectOpen(true);
      }
    },
  }));

  useEffect(() => {
    if (!patientId) return;

    const fetchDocuments = async () => {
      setLoading(true);
      try {
        const docs = await documentsApi.getDocuments(patientId);
        setDocumentList(docs as DocumentDto[]);
      } catch {
        notificationStore.showMessage(
          "Fehler beim Laden der Dokumente",
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [patientId, documentsApi]);

  const handleSelect = async (documentId: string) => {
    const doc = documentList.find((d) => d.id === documentId);
    if (!doc) return;

    setUpdating(true);
    try {
      // Update the document type to Zuzahlungsbefreiung
      if (doc.docuType !== DocumentDtoDocuTypeEnum.Zuzahlungsbefreiung) {
        await documentsApi.updateDocument(documentId, {
          docuType: DocumentDtoDocuTypeEnum.Zuzahlungsbefreiung,
        });
      }

      const displayName = doc.label || doc.name?.split("/").pop() || "Dokument";
      onSelect(documentId, displayName);
      notificationStore.showMessage("Dokument verknüpft", "success");
    } catch {
      notificationStore.showMessage(
        "Fehler beim Verknüpfen des Dokuments",
        "error",
      );
    } finally {
      setUpdating(false);
    }
  };

  const availableDocuments = documentList.filter(
    (doc) => !excludeDocumentIds.includes(doc.id),
  );

  if (loading) {
    return <CircularProgress size={20} />;
  }

  if (availableDocuments.length === 0) {
    return null;
  }

  return (
    <FormControl
      size="small"
      disabled={disabled || updating}
      sx={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0 }}
    >
      <Select
        open={selectOpen}
        onOpen={() => setSelectOpen(true)}
        onClose={() => setSelectOpen(false)}
        value=""
        onChange={(e) => handleSelect(e.target.value as string)}
        MenuProps={{ anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
      >
        {availableDocuments.map((doc) => (
          <MenuItem key={doc.id} value={doc.id}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                {doc.label || doc.name?.split("/").pop() || "Unbenannt"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.docuType}
              </Typography>
              {doc.createdAt && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(doc.createdAt).toLocaleDateString("de-DE")}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

export default ExistingDocumentPicker;
